import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  HiOutlineSearch,
  HiOutlineStar,
  HiOutlineOfficeBuilding,
  HiOutlineChevronRight
} from 'react-icons/hi'
import projectMindmapService from '../services/projectMindmapService'

/**
 * ProjectMindmap - List all projects for mindmap view
 * Public page accessible without login
 */
export default function ProjectMindmap() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredProjects, setFilteredProjects] = useState([])

  useEffect(() => {
    loadProjects()
  }, [])

  useEffect(() => {
    // Filter projects by search query
    if (!searchQuery.trim()) {
      setFilteredProjects(projects)
    } else {
      const query = searchQuery.toLowerCase()
      setFilteredProjects(
        projects.filter(p => p.name.toLowerCase().includes(query))
      )
    }
  }, [searchQuery, projects])

  const loadProjects = async () => {
    setLoading(true)
    const result = await projectMindmapService.getProjects()
    if (result.status === 'success') {
      setProjects(result.data || [])
    }
    setLoading(false)
  }

  // Separate featured and normal projects
  const featuredProjects = filteredProjects.filter(p => p.is_featured)
  const normalProjects = filteredProjects.filter(p => !p.is_featured)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-xl font-bold text-gray-900 mb-3">
            Dự án công ty
          </h1>

          {/* Search */}
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm dự án..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <HiOutlineOfficeBuilding className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">
              {searchQuery ? 'Không tìm thấy dự án phù hợp' : 'Chưa có dự án nào'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Featured Projects */}
            {featuredProjects.length > 0 && (
              <div>
                <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <HiOutlineStar className="w-4 h-4 text-amber-500" />
                  Dự án nổi bật
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {featuredProjects.map(project => (
                    <ProjectCard key={project.id} project={project} featured />
                  ))}
                </div>
              </div>
            )}

            {/* All Projects */}
            {normalProjects.length > 0 && (
              <div>
                {featuredProjects.length > 0 && (
                  <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                    Tất cả dự án
                  </h2>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {normalProjects.map(project => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * ProjectCard - Single project card
 */
function ProjectCard({ project, featured = false }) {
  const imageUrl = project.avatar_url || '/placeholder-project.jpg'

  return (
    <Link
      to={`/projects/${project.slug}`}
      className={`
        block bg-white rounded-lg sm:rounded-xl border overflow-hidden
        hover:shadow-md hover:border-primary-200 transition-all
        ${featured ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-200'}
      `}
    >
      {/* Image */}
      <div className="relative h-28 sm:h-40 bg-gray-100">
        <img
          src={imageUrl}
          alt={project.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = '/placeholder-project.jpg'
          }}
        />
        {featured && (
          <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-amber-500 text-white text-2xs sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1">
            <HiOutlineStar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">Nổi bật</span>
          </div>
        )}
        {project.branch_count > 0 && (
          <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2 bg-black/60 text-white text-2xs sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
            {project.branch_count} nhánh
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2.5 sm:p-4">
        <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2 mb-1 sm:mb-2">
          {project.name}
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-xs sm:text-sm text-gray-500">
            Xem chi tiết
          </span>
          <HiOutlineChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
        </div>
      </div>
    </Link>
  )
}
